'use client';

import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import Link from 'next/link';
import { useAuthMutations } from '@/lib/queries/auth.mutations';

const forgotPasswordSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email' }),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordForm() {
  const { forgotPassword, isForgotPasswordPending } = useAuthMutations();
  const [isSuccess, setIsSuccess] = useState(false);

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  async function onSubmit(values: ForgotPasswordFormValues) {
    setIsSuccess(false); // Reset success state on new submission
    form.clearErrors(); // Clear previous errors
    try {
      const result = await forgotPassword(values.email);

      if (result.error) {
        // Handle specific or general errors from API
        form.setError('root', {
          type: 'manual',
          message:
            Array.isArray(result.error?.general) && result.error.general.length > 0
              ? result.error.general[0]
              : 'Failed to send password reset email. Please try again.',
        });
      } else {
        // Success
        setIsSuccess(true);
        form.reset(); // Clear the form on success
      }
    } catch (error) {
      console.error('Forgot password submission error:', error);
      form.setError('root', {
        type: 'manual',
        message: 'An unexpected network or server error occurred.',
      });
    }
  }

  return (
    <div className="w-full max-w-[420px] mx-auto">
      <div className="mb-8 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">Forgot Password</h1>
        <p className="text-sm text-muted-foreground mt-2">Enter your email to receive reset instructions</p>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {form.formState.errors.root && !isSuccess && (
            <Alert variant="destructive" className="text-sm">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{form.formState.errors.root.message}</AlertDescription>
            </Alert>
          )}
          {isSuccess && (
            <Alert variant="default" className="text-sm border-green-500 text-green-700">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <AlertDescription>Password reset instructions sent to your email.</AlertDescription>
            </Alert>
          )}

          {!isSuccess && (
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="you@example.com" {...field} className="h-11" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {!isSuccess && (
            <Button type="submit" className="w-full h-11" disabled={isForgotPasswordPending}>
              {isForgotPasswordPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Instructions'
              )}
            </Button>
          )}

          <p className="text-sm text-center text-muted-foreground">
            Remembered your password?{' '}
            <Link href="/login" className="text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </form>
      </Form>
    </div>
  );
}
